import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faIndustry, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { SelectField } from 'modules/Common/form/v2';
import { useSupplierInbox } from 'modules/FreeSupplier/useSupplierInbox';
import {
  RequestLineInstallationResolution,
  findSupplierInboxRequest,
  formatRequestInterval,
  resolveRequestLineInstallationState,
} from 'modules/FreeSupplier/supplierInboxPresentation';
import {
  defaultDirectEntryLineIndex,
  resolveRequestLineEntrySeed,
} from 'modules/FreeSupplier/supplierRequestEntrySeed';
import { useSupplierFactorActions } from 'modules/FreeSupplier/useSupplierFactors';
import { useCnGoods } from 'modules/Common/utils/Utility';
import { SupplierRoutePath, buildSupplierRequestReviewPath } from 'modules/FreeSupplier/routes';
import useCnGoodLabel from '../state/useCnGoodLabel';
import { useMockData } from '../state/MockDataContext';
import SupplierEmissionsModal, { SupplierSaveMode } from '../components/SupplierEmissionsModal';
import { SupplierPanelLoader } from '../components/SupplierLoadingStates';

// Item 16B: SelectField option labels can't render JSX — `cnGoodLabel` (and
// now `t`) are threaded in from the component (hooks can only be called
// there).
const lineOptionLabel = (line, cnGoodLabel, t) => {
  const fulfilled = line.fulfilment?.state === 'fulfilled';
  const state = fulfilled
    ? t('freeSupplier:requestDirectEntry.linePicker.fulfilled', 'Fulfilled')
    : t('freeSupplier:requestDirectEntry.linePicker.unfulfilled', 'Unfulfilled');
  return `${cnGoodLabel(line.cnCode)} · ${formatRequestInterval(line)} · ${state}`;
};

// Request-tailored direct entry (/supplier/requests/:requestId/direct-entry).
// Opens the re-pointed emissions modal seeded from a request line (CN code,
// interval, and — when the request scoped them — product/method, A5) so the
// published factor targets the requested series. Multi-line requests get a
// line picker defaulting to the first unfulfilled line (A4) — the modal only
// opens once the supplier confirms via "Continue" (item 6); single-line
// requests keep auto-open. The target installation is resolved by ID exactly
// as the backend fulfilment matcher resolves it — an explicit tri-state
// (item 11) distinguishes a resolvable match from an ambiguous unscoped line
// (several installations, needs a pick) from a scoped line whose named
// installation no longer exists (item 4: never offered a substitute — a
// different site can never fulfil a scoped request). On success the factor
// list + inbox refetch and we return to the emissions table.
//
// Q3 (S7 fix batch 3): the CN good is ALSO resolved against the real catalog
// (resolveRequestLineCnGood) — a full catalog code preselects cleanly, but a
// chapter/heading-level code (e.g. "7208") is a PREFIX many specific catalog
// goods share, so CnGoodField's exact-match lookup can never resolve it on
// its own; auto-picking one would silently guess what the supplier produces.
// Left unselected with a visible hint instead (SupplierEmissionsModal's
// `contextNotice`).
export default function RequestDirectEntryPage() {
  const { t } = useTranslation('freeSupplier');
  const { requestId } = useParams();
  const history = useHistory();
  const inbox = useSupplierInbox();
  const { operator, installationDocs } = useMockData();
  const cnGoods = useCnGoods();
  const cnGoodLabel = useCnGoodLabel();
  const { invalidate } = useSupplierFactorActions();
  const [open, setOpen] = useState(false);
  const [selectedLineIndex, setSelectedLineIndex] = useState(0);
  // Only meaningful for a genuinely-unscoped line with SEVERAL installations
  // to choose from (selection_required, item 11) — a scoped line that can't
  // resolve (unresolvable_scoped) never offers a substitute (item 4).
  const [manualInstallationId, setManualInstallationId] = useState('');

  const connections = inbox.data?.items || [];
  const match = findSupplierInboxRequest(connections, requestId);
  const lines = useMemo(() => match?.request?.lines || [], [match]);

  // Default to the first unfulfilled line whenever the request (re)loads or
  // changes — a manual pick afterwards is never overwritten by this.
  useEffect(() => {
    if (lines.length > 0) setSelectedLineIndex(defaultDirectEntryLineIndex(lines));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, lines.length]);

  // The manual installation fallback is scoped to the selected line — reset
  // it whenever the line (or request) changes.
  useEffect(() => {
    setManualInstallationId('');
  }, [requestId, selectedLineIndex]);

  // Item 6: single-line requests auto-open the modal (unchanged); multi-line
  // requests wait for an explicit "Continue" on the line picker.
  useEffect(() => {
    if (lines.length === 1) setOpen(true);
  }, [lines.length]);

  const line = lines[selectedLineIndex] || null;
  // Q3: resolveRequestLineEntrySeed overrides the seed's CN good with the
  // resolved catalog match when exactly one exists — the raw request line's
  // code (possibly chapter/heading-level) never resolves on its own in
  // CnGoodField; `needsCnGoodPick` flags the rest for the hint below.
  const { seed, needsCnGoodPick } = useMemo(
    () => resolveRequestLineEntrySeed(line, cnGoods),
    [line, cnGoods],
  );
  const cnGoodHint =
    line && needsCnGoodPick
      ? t(
          'freeSupplier:requestDirectEntry.cnGoodHint',
          'Requested: {{cnGood}} — pick the specific good you produce.',
          { cnGood: cnGoodLabel(line.cnCode) },
        )
      : undefined;

  const docs = installationDocs || [];
  const resolution = line ? resolveRequestLineInstallationState(line, docs) : null;
  const installation =
    resolution?.state === RequestLineInstallationResolution.Matched
      ? resolution.installation
      : resolution?.state === RequestLineInstallationResolution.SelectionRequired
        ? docs.find((d) => String(d._id) === manualInstallationId) || null
        : null;

  // Item 6: closing the modal in multi-line mode returns to the picker
  // (there may be other lines still to enter) instead of navigating away;
  // single-line requests keep the original "back to the emissions table"
  // behavior.
  const closeModal = () => {
    setOpen(false);
    if (lines.length <= 1) history.push(SupplierRoutePath.EmissionFactors);
  };

  if (inbox.isPending) {
    return (
      <SupplierPanelLoader
        ariaLabel={t(
          'freeSupplier:requestDirectEntry.loading.ariaLabel',
          'Loading this data request',
        )}
        messages={[
          t('freeSupplier:requestDirectEntry.loading.message1', 'Opening the data request'),
          t(
            'freeSupplier:requestDirectEntry.loading.message2',
            'Reading the CN codes and reporting period',
          ),
          t('freeSupplier:requestDirectEntry.loading.message3', 'Preparing your entry form'),
        ]}
      />
    );
  }

  // Item 8: a query ERROR is not "not found" — offer a retry instead of the
  // permanent-looking "we couldn't find that request" copy below.
  if (inbox.isError) {
    return (
      <div className="rounded-2xl border border-dashed border-red-300 p-10 text-center">
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          className="text-red-300 text-2xl mb-3"
        />
        <p className="text-sm text-slate-600 m-0">
          {t('freeSupplier:requestDirectEntry.loadError.title', 'We couldn’t load this request.')}
        </p>
        <p className="text-xs text-slate-400 mt-1 m-0">
          {t(
            'freeSupplier:requestDirectEntry.loadError.hint',
            'Check your connection and try again.',
          )}
        </p>
        <CgRadixButton
          variant="outline"
          size="sm"
          className="mt-4"
          onClick={() => inbox.refetch()}
        >
          {t('freeSupplier:requestDirectEntry.loadError.retry', 'Retry')}
        </CgRadixButton>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
        <p className="text-sm text-slate-600 m-0">
          {t('freeSupplier:requestDirectEntry.notFound.title', 'We couldn’t find that request.')}
        </p>
        <Link
          to={SupplierRoutePath.Inbox}
          className="inline-flex items-center gap-1.5 mt-4 text-xs font-semibold text-primaryColor no-underline"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('freeSupplier:requestDirectEntry.notFound.backToRequests', 'Back to Data Requests')}
        </Link>
      </div>
    );
  }

  if (docs.length === 0) {
    return (
      <div className="flex flex-col gap-4 max-w-xl w-full mx-auto">
        <Link
          to={buildSupplierRequestReviewPath(requestId)}
          className="text-xs text-slate-400 no-underline hover:text-slate-600 inline-flex items-center gap-1.5"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('freeSupplier:requestDirectEntry.backToRequest', 'Back to request')}
        </Link>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <div className="w-12 h-12 rounded-2xl bg-primaryColor/10 flex items-center justify-center mx-auto mb-3">
            <FontAwesomeIcon
              icon={faIndustry}
              className="text-primaryColor"
            />
          </div>
          <p className="text-sm font-semibold text-slate-800 m-0">
            {t('freeSupplier:requestDirectEntry.noInstallation.title', 'Add an installation first')}
          </p>
          <p className="text-xs text-slate-500 mt-1 mb-4">
            {t(
              'freeSupplier:requestDirectEntry.noInstallation.description',
              'Emissions data belongs to a production site. Add the installation this good comes from, then enter its emissions.',
            )}
          </p>
          <CgRadixButton
            variant="primary"
            size="sm"
            onClick={() => history.push(SupplierRoutePath.Installations)}
          >
            {t('freeSupplier:requestDirectEntry.noInstallation.cta', 'Add installation')}
          </CgRadixButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 max-w-xl w-full mx-auto">
      <Link
        to={buildSupplierRequestReviewPath(requestId)}
        className="text-xs text-slate-400 no-underline hover:text-slate-600 inline-flex items-center gap-1.5"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        {t('freeSupplier:requestDirectEntry.backToRequest', 'Back to request')}
      </Link>

      {/* A4/item-6: multi-line requests get an explicit line picker, showing
          CN code + period + fulfilment state per line — defaulted above to
          the first unfulfilled one — plus a resolved-line summary and an
          explicit Continue that opens the modal (it does NOT auto-open over
          the picker). Single-line requests keep the previous behavior (no
          picker, the one line is implicit, auto-open). */}
      {lines.length > 1 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold text-slate-700 m-0 mb-2">
            {t(
              'freeSupplier:requestDirectEntry.linePicker.question',
              'Which line are you entering?',
            )}
          </p>
          <SelectField
            id="requestDirectEntryLine"
            field={{
              name: 'lineIndex',
              value: String(selectedLineIndex),
              onChange: (v) => setSelectedLineIndex(Number(v)),
              onBlur: () => {},
            }}
            options={lines.map((l, idx) => ({
              value: String(idx),
              label: lineOptionLabel(l, cnGoodLabel, t),
            }))}
            placeholder={t(
              'freeSupplier:requestDirectEntry.linePicker.placeholder',
              'Choose a line',
            )}
          />
          {line ? (
            <p className="text-xs text-slate-500 mt-2 mb-0">
              {cnGoodLabel(line.cnCode)} · {formatRequestInterval(line)}
              {installation ? ` · ${installation.name}` : ''}
            </p>
          ) : null}
          <div className="mt-3">
            <CgRadixButton
              variant="primary"
              size="sm"
              disabled={!installation}
              onClick={() => setOpen(true)}
            >
              {t('freeSupplier:requestDirectEntry.linePicker.continue', 'Continue')}
            </CgRadixButton>
          </div>
        </div>
      ) : null}

      {/* Item 11: a genuinely-unscoped line with several installations to
          choose from — never auto-selected, the supplier picks explicitly. */}
      {resolution?.state === RequestLineInstallationResolution.SelectionRequired ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold text-slate-700 m-0">
            {t(
              'freeSupplier:requestDirectEntry.installationPicker.question',
              'Which installation does this apply to?',
            )}
          </p>
          <p className="text-[11px] text-slate-500 mt-1 mb-3">
            {t(
              'freeSupplier:requestDirectEntry.installationPicker.hint',
              'This request doesn’t name a specific site. Choose the installation this data comes from.',
            )}
          </p>
          <SelectField
            id="requestDirectEntryInstallation"
            field={{
              name: 'installationId',
              value: manualInstallationId,
              onChange: setManualInstallationId,
              onBlur: () => {},
            }}
            options={docs.map((d) => ({ value: String(d._id), label: d.name }))}
            placeholder={t(
              'freeSupplier:requestDirectEntry.installationPicker.placeholder',
              'Choose an installation',
            )}
          />
        </div>
      ) : null}

      {/* Item 4 (REVERSED ruling): a scoped line whose named installation no
          longer exists in this workspace can NEVER be fulfilled against a
          substitute — the backend matches by the exact installation id. Block
          publication for this line with a clear error instead of offering a
          picker; in multi-line requests the picker above lets the supplier
          switch to a different (resolvable) line. */}
      {resolution?.state === RequestLineInstallationResolution.UnresolvableScoped ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-semibold text-amber-800 m-0 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {t(
              'freeSupplier:requestDirectEntry.unresolvableScoped.title',
              'Can’t fulfil this line',
            )}
          </p>
          <p className="text-[11px] text-amber-700 mt-1 mb-0">
            {t(
              'freeSupplier:requestDirectEntry.unresolvableScoped.body',
              'The requested installation no longer exists in your workspace — the declarant’s request cannot be fulfilled against a different site.',
            )}{' '}
            {lines.length > 1
              ? t(
                  'freeSupplier:requestDirectEntry.unresolvableScoped.multiLineHint',
                  'Choose a different line above, or contact the declarant to update the request.',
                )
              : t(
                  'freeSupplier:requestDirectEntry.unresolvableScoped.singleLineHint',
                  'Contact the declarant to update the request.',
                )}
          </p>
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <p className="text-sm font-semibold text-slate-800 m-0">
          {t(
            'freeSupplier:requestDirectEntry.enterEmissionsFor',
            'Enter emissions for {{declarantName}}’s request',
            { declarantName: match.connection.declarant.name },
          )}
        </p>
        {/* Q3: only claim what we actually pre-fill — the reporting period
            always does; the CN good only does when the request named a
            single, specific catalog good (cnGoodHint covers the rest). A
            request is only fulfilled by PUBLISHED data (item B/S10), so this
            flow defaults to "Publish now" — "Save as draft" stays one click
            away for a supplier who isn't ready to share yet. */}
        <p className="text-xs text-slate-500 mt-1 m-0">
          {cnGoodHint
            ? t(
                'freeSupplier:requestDirectEntry.prefillNoticeCnGoodOnly',
                'The form is pre-filled with the requested reporting period. Publish now to share it with this customer, or save it as a draft to confirm later.',
              )
            : t(
                'freeSupplier:requestDirectEntry.prefillNotice',
                'The form is pre-filled with the requested CN code and reporting period. Publish now to share it with this customer, or save it as a draft to confirm later.',
              )}
        </p>
      </div>

      {installation ? (
        <SupplierEmissionsModal
          show={open}
          onClose={closeModal}
          operator={operator}
          installation={installation}
          selectedEmission={seed}
          contextNotice={cnGoodHint}
          handleRefresh={invalidate}
          defaultSaveMode={SupplierSaveMode.Publish}
        />
      ) : null}
    </div>
  );
}
